/**
 * PostRecoveryOptions — Sprint 13 S13.10b (motor v10 §1).
 *
 * Pantalla que se muestra cuando una semana de recuperación ha terminado
 * (`postRecoveryOptionsAvailable`). El atleta elige entre 2 caminos:
 *   1. Preparar una carrera → flujo de crear Plan de Carrera (CreatePlanPage,
 *      vía /plan/crear — reusa el formulario existente).
 *   2. Seguir progresando → Plan de Progresión: reusa el componente
 *      `ProgressionDistanceChoice` (elige distancia → POST progression).
 *
 * Reusa componentes/flujos existentes en lugar de duplicar formularios.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { t } from "@/i18n/es";
import { ProgressionDistanceChoice } from "@/pages/Plan/components/ProgressionDistanceChoice";

interface Props {
  /** Llamado tras crear una nueva progresión (el contenedor recarga). */
  onCreated: () => void;
}

export function PostRecoveryOptions({ onCreated }: Props) {
  const navigate = useNavigate();
  const [showProgressionChoice, setShowProgressionChoice] = useState(false);

  // Sub-pantalla "seguir progresando" → reusa ProgressionDistanceChoice.
  if (showProgressionChoice) {
    return <ProgressionDistanceChoice onCreated={onCreated} />;
  }

  return (
    <main
      data-testid="post-recovery-options"
      className="mx-auto flex min-h-app max-w-md flex-col gap-6 bg-graphite p-6 text-bone"
    >
      <header>
        <p className="text-xs uppercase tracking-widest text-gray-mid">
          {t.plan.pageTitle.toLowerCase()}
        </p>
        <h1 className="mt-6 text-2xl font-medium tracking-tight">
          {t.plan.postRecoveryTitle}
        </h1>
        <p className="mt-2 text-sm text-bone-muted">
          {t.plan.postRecoverySubtitle}
        </p>
      </header>

      <div className="flex flex-col gap-3">
        <button
          type="button"
          data-testid="post-recovery-race"
          onClick={() => navigate("/plan/crear")}
          className="rounded-md border border-border-subtle bg-carbon-raised px-4 py-3 text-left text-sm text-bone transition-colors duration-fast hover:border-border-strong"
        >
          <span className="block font-medium">
            {t.plan.postRecoveryRaceTitle}
          </span>
          <span className="mt-1 block text-xs text-bone-muted">
            {t.plan.postRecoveryRaceDesc}
          </span>
        </button>

        <button
          type="button"
          data-testid="post-recovery-progression"
          onClick={() => setShowProgressionChoice(true)}
          className="rounded-md border border-border-subtle bg-carbon-raised px-4 py-3 text-left text-sm text-bone transition-colors duration-fast hover:border-border-strong"
        >
          <span className="block font-medium">
            {t.plan.postRecoveryProgressionTitle}
          </span>
          <span className="mt-1 block text-xs text-bone-muted">
            {t.plan.postRecoveryProgressionDesc}
          </span>
        </button>
      </div>
    </main>
  );
}
